import React, { ReactElement, useEffect } from 'react';
import { ScreenConfigBase, ScreenType, SharedScreenName } from '../../../config/FlowTypes';
import { useAppStore } from '../../../store/Store';
import { dispatchGrantUs65PlusAccess } from '../../../store/us65Plus/Us65PlusActions';
import { isEligibleForUs65RevCatGrant, isMedicareAdvantage } from '../../../util/MedicareAccess';
import { ScreenWrapper } from '../../styles/Containers';
import { NavigationProps } from '../FlowNavigation';
import { PresentationLayout } from '../layouts/PresentationLayout';

export type Us65PlusGrantAccessScreenConfig<ScreenNames extends string> = ScreenConfigBase<
  ScreenType.Us65PlusGrantAccess,
  ScreenNames
>;

type Props<ScreenNames extends string> = NavigationProps<
  Us65PlusGrantAccessScreenConfig<ScreenNames>,
  ScreenNames
>;

/** Loading screen to wait for US 65+ grant access to complete if not already. */
export const Us65PlusGrantAccessScreen = <ScreenNames extends string>(
  props: Props<ScreenNames>
): ReactElement => {
  const { granted, loading, error } = useAppStore((state) => state.us65Plus);
  const isEligibleForGrant = useAppStore((state) => isEligibleForUs65RevCatGrant(state));
  const isAdvantage = useAppStore((state) => isMedicareAdvantage(state));

  useEffect(
    function startGrantIfNeeded() {
      if (isEligibleForGrant && !granted && !loading && error === undefined) {
        dispatchGrantUs65PlusAccess();
      }
    },
    [isEligibleForGrant, error, granted, loading]
  );

  useEffect(
    function navigateWhenGranted() {
      if (granted) {
        props.navigation.next(props);
      }
    },
    [granted]
  );

  useEffect(
    function navigateToPaymentWhenIneligible() {
      // D2C Medicare Advantage users without a referral pay at checkout; Original users reach
      // this screen only after CMS eligibility and should never be sent to payment here.
      if (!isEligibleForGrant && !granted && isAdvantage) {
        props.navigation.goTo(SharedScreenName.Payment as ScreenNames);
      }
    },
    [isAdvantage, isEligibleForGrant, granted]
  );

  const showError = error !== undefined && !loading;

  return (
    <ScreenWrapper>
      {showError ? (
        <PresentationLayout
          title="Something went wrong"
          content={[{ type: 'text', content: error.message }]}
          primaryButtonTitle="Try again"
          onPressPrimaryButton={dispatchGrantUs65PlusAccess}
        />
      ) : (
        <PresentationLayout
          title="Setting up your access..."
          content={[{ type: 'text', content: 'This usually only takes a moment.' }]}
        />
      )}
    </ScreenWrapper>
  );
};
